import axiosInstance from '../../utils/axiosInstanse';
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import useIsMobile from '../../utils/useIsMobile';
import Spiner from '../Spiner/Spiner';
import host from "../../host";
import Card from "./Card";
import './pass_activate.css';

function Archive() {
  const [notes, setNotes] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(undefined);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const isMobile = useIsMobile(1268);

  const fetchApplications = (pageNum) => {
    setIsLoading(true);
    axiosInstance
      .get(`${host}api/v2/get_tpass_by_user/`, { params: { page: pageNum } })
      .then((response) => {
        setNotes(response.data.data || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
        setPage(response.data.page || pageNum);
        setIsLoading(false);
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          setIsError(true);
        }
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchApplications(1);
  }, []);

  const groupApplicationsById = (apps) => {
    const grouped = {};
    apps.forEach(app => {
      const id = app.ID;
      if (!grouped[id]) {
        grouped[id] = {
          ...app,
          GUEST_ORGANIZATION: app.GUEST_ORGANIZATION,
          GUESTS: [{
            name: app.GUEST_NAME,
            comment: app.APPL_COMMENT || '',
          }],
        };
      } else {
        const exists = grouped[id].GUESTS.some(g => g.name === app.GUEST_NAME);
        if (!exists && app.GUEST_NAME) {
          grouped[id].GUESTS.push({
            name: app.GUEST_NAME,
            comment: app.APPL_COMMENT || '',
          });
        }
      }
    });
    return Object.values(grouped);
  };

  const groupedApplications = useMemo(() => {
    const grouped = groupApplicationsById(filteredApplications);
    return grouped.sort((a, b) => b.ID - a.ID);
  }, [filteredApplications]);

  const getBadgeClass = (status) => {
    switch (status) {
      case 'APPROVED': return 'pal-badge--approved';
      case 'DECLINED': return 'pal-badge--rejected';
      case '': return 'pal-badge--pending';
      default: return 'pal-badge--default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'APPROVED': return 'Одобрено';
      case 'DECLINED': return 'Отклонено';
      case '': return 'В процессе';
      default: return status || 'Не определено';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit' });
  };

  // Рендер для мобильных устройств
  const renderMobileView = () => (
    <div className="pal-mobile-list">
      {notes.map((app) => {
        return (
          <motion.div
            key={app.ID}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`pal-card`}
          >
            <div className="pal-card-header">
              <div>
                <div className="pal-card-name">
                  {app.GUESTS?.length > 1
                    ? `${app.GUESTS[0].name} +${app.GUESTS.length - 1}`
                    : app.GUEST_NAME || '—'
                  }
                </div>
                <div className="pal-card-id">ID: {app.ID}</div>
              </div>
              <span className={`pal-badge ${getBadgeClass(app.ORIGINAL_STATUS)}`}>
                {getStatusText(app.ORIGINAL_STATUS)}
              </span>
            </div>

            {app.GUEST_ORGANIZATION && (
              <div className="pal-card-org">
                <strong>Организация:</strong> {app.GUEST_ORGANIZATION}
              </div>
            )}

            {app.GUESTS?.length > 1 && (
              <div className="pal-card-guests">
                <div className="pal-card-guests-title">Гости ({app.GUESTS.length}):</div>
                <ul>
                  {app.GUESTS.map((g, idx) => (
                    <li key={idx}>
                      {g.name}
                      {g.comment && <span className="pal-guest-sub"> — <em>{g.comment}</em></span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pal-card-details">
              <div><strong>Цель:</strong> {app.VISIT_PURPOSE || '—'}</div>
              <div><strong>Место:</strong> {app.VISIT_PLACE_NAME || '—'}</div>
              <div><strong>К кому:</strong> {app.COMETO_DESCRIPTION || '—'}</div>
              <div><strong>Период:</strong> {formatDateShort(app.PERIOD_BEGIN)} — {formatDateShort(app.PERIOD_END)}</div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );

  // Рендер для десктопа
  const renderDesktopView = () => (
    <div className="pal-table-wrap">
      <div className="pal-table-scroll">
        <table className="pal-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>ФИО гостя</th>
              <th>Организация</th>
              <th>Цель визита</th>
              <th>Место</th>
              <th>К кому</th>
              <th>Статус</th>
              <th>Период</th>
            </tr>
          </thead>
          <tbody>
            {notes.map((app) => {
              return (
                <tr
                  key={app.ID}
                  className={``}
                >
                  <td>{app.ID}</td>
                  <td>
                    <div>{app.GUESTS?.[0]?.name || app.GUEST_NAME || '—'}</div>
                    {app.GUESTS?.length > 1 && (
                      <div className="pal-guest-more">+{app.GUESTS.length - 1} ещё</div>
                    )}
                  </td>
                  <td>{app.GUEST_ORGANIZATION || '—'}</td>
                  <td>{app.VISIT_PURPOSE || '—'}</td>
                  <td>{app.VISIT_PLACE_NAME || '—'}</td>
                  <td>{app.COMETO_DESCRIPTION || '—'}</td>
                  <td>
                    <span className={`pal-badge ${getBadgeClass(app.ORIGINAL_STATUS)}`}>
                      {getStatusText(app.ORIGINAL_STATUS)}
                    </span>
                  </td>
                  <td>
                    <div>{formatDate(app.PERIOD_BEGIN)}</div>
                    <div>{formatDate(app.PERIOD_END)}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      {isLoading ? (
        <Spiner />
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.12 }}
        >
          {isError ? (
            <div className="pa-access-denied">У вас нет прав для доступа к данному разделу</div>
          ) : notes.length > 0 ? (
            <div className='pal-container'>
              <div className="pal-result-count">Найдено: {total} заявок</div>
              {isMobile ? renderMobileView() : renderDesktopView()}
              {totalPages > 1 && (
                <div className="pal-pagination">
                  <button
                    className="pal-pagination-btn"
                    disabled={page <= 1}
                    onClick={() => fetchApplications(page - 1)}
                  >
                    ←
                  </button>
                  <span className="pal-pagination-info">
                    Стр. {page} из {totalPages}
                  </span>
                  <button
                    className="pal-pagination-btn"
                    disabled={page >= totalPages}
                    onClick={() => fetchApplications(page + 1)}
                  >
                    →
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="pa-empty">У вас нет заявок</div>
          )}

          <div style={{ marginBottom: 70 }}></div>
        </motion.div>
      )}
    </div>
  );
}

export default Archive;